import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import api from "../../services/api";
import { groupPeriodsByUser, resolveMonthlyCost } from "../../utils/user_contract";
import Loader from "../../components/loader";
import PnlDocumentation from "./documentation";
import PnlDetailModal from "./detail-modal";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const CURRENT_MONTH = new Date().getMonth();
const CURRENT_YEAR = new Date().getFullYear();

const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(n));

const STATUS_TONE = {
  pending: "bg-orange-100", // at least one PENDING payment → amount not certain
  topay: "bg-yellow-100", // some TOPAY (scheduled), none pending
};

// Group records by month index, summing a field
function monthlySum(records, dateField, valueField) {
  const sums = new Array(12).fill(0);
  for (const r of records) {
    const d = new Date(r[dateField]);
    const m = d.getMonth();
    sums[m] += r[valueField] || 0;
  }
  return sums;
}

export default function Pnl() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [tab, setTab] = useState("table");
  const [loading, setLoading] = useState(false);
  const { businessUnit } = useSelector((state) => state.Auth);

  // Raw data from API
  const [rawData, setRawData] = useState({ invoices: [], activities: [], banks: [], employees: [] });

  // Modal state
  const [modal, setModal] = useState({ open: false, rowLabel: null, monthIndex: null });

  useEffect(() => {
    fetchData();
  }, [year, businessUnit._id]);

  async function fetchData() {
    setLoading(true);
    try {
      const dateFrom = new Date(year, 0, 1);
      const dateTo = new Date(year, 11, 31);
      const buFilter = businessUnit._id !== "ALL" ? { business_unit_id: businessUnit._id } : {};

      // No status filter on users: we want people who were active in past months even if they are now inactive.
      // The months they count for are bounded by starting_date/exit_date (employees) or by their real payments (freelances).
      const [invoicesRes, activitiesRes, banksRes, employeesRes, freelanceMonthlyRes, paymentsRes, contractsRes] = await Promise.all([
        api.post("/invoice/search", { ...buFilter, sent: "yes", dateFrom, dateTo, limit: 10000 }),
        api.post("/activity/search", { ...buFilter, dateFrom, dateTo, limit: 10000 }),
        api.post("/bank/search", { ...buFilter, dateFrom, dateTo, per_page: 10000 }),
        api.post("/user/search", {
          ...buFilter,
          contracts: ["VIE", "CDD", "CDI", "APPRENTICESHIP", "INTERNSHIP", "Me kohe te plote"],
          role: { $in: ["admin", "user", "super_admin"] },
          per_page: 10000,
        }),
        api.post("/user/search", {
          ...buFilter,
          contracts: ["FREELANCE"],
          payment_type: "MONTHLY",
          role: { $in: ["admin", "user", "super_admin"] },
          per_page: 10000,
        }),
        api.post("/payment/search", { type: "activity", status: { $nin: ["CANCELED", "ERROR"] }, dateFrom, dateTo, limit: 10000 }),
        api.post("/user_contract/search", { ...buFilter, limit: 10000 }),
      ]);

      setRawData({
        invoices: invoicesRes.ok ? invoicesRes.data : [],
        activities: activitiesRes.ok ? activitiesRes.data : [],
        banks: banksRes.ok ? (banksRes.data?.banks || banksRes.data || []) : [],
        employees: employeesRes.ok ? (employeesRes.data?.users || employeesRes.data || []) : [],
        freelanceMonthly: freelanceMonthlyRes.ok ? (freelanceMonthlyRes.data?.users || freelanceMonthlyRes.data || []) : [],
        payments: paymentsRes.ok ? (paymentsRes.data?.payments || paymentsRes.data || []) : [],
        contracts: contractsRes.ok ? (contractsRes.data || []) : [],
      });
    } catch (e) {
      console.error("PNL fetch error", e);
    }
    setLoading(false);
  }

  // Compute monthly arrays from raw data
  const computed = useMemo(() => {
    const { invoices, activities, banks, employees, freelanceMonthly = [], payments = [], contracts = [] } = rawData;
    const periodsByUser = groupPeriodsByUser(contracts);

    // A written-off invoice was sent, but the cash will not come. It stays unpaid in
    // recouvrement and drops out of invoiced revenue.
    const invoiceIncome = monthlySum(
      invoices.filter((inv) => !inv.written_off),
      "date",
      "netTotal",
    );
    const bankIncome = monthlySum(
      banks.filter((b) => b.category === "INCOME" && b.business_unit_name === "Walego"),
      "date",
      "amount",
    );
    const income = invoiceIncome.map((amount, monthIndex) => amount + bankIncome[monthIndex]);

    const grants = monthlySum(
      banks.filter((b) => b.sub_category === "SUBVENTION"),
      "date",
      "amount",
    );

    // Employee Costs: for each employee active during a month, add the cost of the
    // user_contract period active that month (falls back to the flat snapshot).
    const employeeCosts = new Array(12).fill(0);
    for (const emp of employees) {
      const startDate = emp.starting_date ? new Date(emp.starting_date) : null;
      const exitDate = emp.exit_date ? new Date(emp.exit_date) : null;
      for (let m = 0; m < 12; m++) {
        const monthStart = new Date(year, m, 1);
        const monthEnd = new Date(year, m + 1, 0);
        if (startDate && startDate > monthEnd) continue;
        if (exitDate && exitDate < monthStart) continue;
        employeeCosts[m] += resolveMonthlyCost(periodsByUser, emp, monthEnd);
      }
    }

    // Freelance Monthly Costs:
    //  - actual months  → real paid amounts (sum of their type "activity" payments): raises aren't retroactive,
    //    pauses cost 0, and people now inactive still count on the months they were paid.
    //  - forecast months → no payments yet, so project the flat total_monthly_cost of still-active freelancers.
    const isForecastMonth = (m) => (year === CURRENT_YEAR ? m > CURRENT_MONTH : year > CURRENT_YEAR);
    const freelanceMonthlyIds = new Set(freelanceMonthly.map((freelancer) => freelancer._id));
    const freelanceMonthlyPayments = payments.filter((payment) => freelanceMonthlyIds.has(payment.user_id));
    const freelanceMonthlyCosts = monthlySum(freelanceMonthlyPayments, "date", "value");

    // Confidence tint per actual month from the worst payment status (PENDING beats TOPAY; all paid → no tint).
    const freelanceMonthlyStatus = new Array(12).fill(null);
    for (const payment of freelanceMonthlyPayments) {
      const monthIndex = new Date(payment.date).getMonth();
      if (isForecastMonth(monthIndex)) continue;
      if (payment.status === "PENDING") freelanceMonthlyStatus[monthIndex] = "pending";
      else if (payment.status === "TOPAY" && freelanceMonthlyStatus[monthIndex] !== "pending") freelanceMonthlyStatus[monthIndex] = "topay";
    }

    // Forecast months: replace the (empty) payment sum with the projected cost of still-active freelancers.
    for (let monthIndex = 0; monthIndex < 12; monthIndex++) {
      if (!isForecastMonth(monthIndex)) continue;
      const monthStart = new Date(year, monthIndex, 1);
      const monthEnd = new Date(year, monthIndex + 1, 0);
      freelanceMonthlyCosts[monthIndex] = 0;
      for (const freelancer of freelanceMonthly) {
        if (freelancer.status !== "active") continue;
        const start = freelancer.starting_date ? new Date(freelancer.starting_date) : null;
        const exit = freelancer.exit_date ? new Date(freelancer.exit_date) : null;
        if (start && start > monthEnd) continue;
        if (exit && exit < monthStart) continue;
        // Forecast uses the period active that (future) month, so a scheduled raise shows up.
        freelanceMonthlyCosts[monthIndex] += resolveMonthlyCost(periodsByUser, freelancer, monthEnd);
      }
    }

    // Freelance Daily Costs: genuinely daily activities (snapshot userPaymentType ≠ MONTHLY — this also keeps
    // out monthly freelancers from other BUs whose activities leak in), AND not anyone who is currently a
    // monthly freelancer here (freelanceMonthlyIds), so the same person can't land in both Daily and Monthly.
    const freelanceCosts = monthlySum(
      activities.filter(
        (activity) =>
          activity.userContract === "FREELANCE" &&
          activity.userPaymentType !== "MONTHLY" &&
          !freelanceMonthlyIds.has(activity.userId) &&
          activity.projectType !== "timeoff",
      ),
      "date",
      "cost",
    );

    const fixedCosts = monthlySum(
      banks.filter((b) => b.category === "GENERAL_COST"),
      "date",
      "amount",
    ).map((v) => v * -1);

    const businessIntroducer = monthlySum(
      banks.filter((b) => b.sub_category === "BUSINESS_INTRODUCER_COST"),
      "date",
      "amount",
    ).map((v) => v * -1);

    // Loan & Taxes are below-the-operating-line items, kept with their real bank sign (an outflow stays negative).
    const loan = monthlySum(
      banks.filter((b) => b.category === "BANK_LOAN"),
      "date",
      "amount",
    );

    const taxes = monthlySum(
      banks.filter((b) => b.category === "TAXES"),
      "date",
      "amount",
    );

    const totalIncome = income.map((amount, monthIndex) => amount + grants[monthIndex]);
    // Operating expenses only — loan (financing) and taxes sit below Operating Profit.
    const totalExpense = income.map((_, monthIndex) => employeeCosts[monthIndex] + freelanceMonthlyCosts[monthIndex] + freelanceCosts[monthIndex] + fixedCosts[monthIndex] + businessIntroducer[monthIndex]);
    const operatingProfit = totalIncome.map((amount, monthIndex) => amount - totalExpense[monthIndex]);
    const netProfit = operatingProfit.map((amount, monthIndex) => amount + loan[monthIndex] + taxes[monthIndex]);

    return { income, grants, totalIncome, employeeCosts, freelanceMonthlyCosts, freelanceMonthlyStatus, freelanceCosts, fixedCosts, businessIntroducer, loan, taxes, totalExpense, operatingProfit, netProfit };
  }, [rawData]);

  const isForecast = (monthIdx) => (year === CURRENT_YEAR ? monthIdx > CURRENT_MONTH : year > CURRENT_YEAR);
  const sum = (arr) => arr.reduce((a, b) => a + b, 0);
  const actualCount = Array.from({ length: 12 }, (_, monthIndex) => monthIndex).filter((monthIndex) => !isForecast(monthIndex)).length;
  const projTotal = (arr) => (actualCount > 0 ? Math.round((sum(arr) / actualCount) * 12) : 0);
  const ytd = (arr) => Math.round(arr.reduce((total, amount, monthIndex) => total + (isForecast(monthIndex) ? 0 : amount), 0));

  const onCellClick = (rowLabel, monthIndex) => setModal({ open: true, rowLabel, monthIndex });

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-xl font-bold text-gray-900">P&L Dashboard</h1>
          <div className="flex bg-gray-100 rounded-lg p-0.5">
            {[
              { key: "table", label: "Table" },
              { key: "doc", label: "Documentation" },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${tab === t.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}>
                {t.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {loading && <span className="text-xs text-gray-400">Loading...</span>}
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium bg-white">
            {[2024, 2025, 2026].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Documentation Tab */}
      {tab === "doc" && <PnlDocumentation />}

      {/* Table Tab */}
      {tab === "table" && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <div className="flex items-center gap-4 px-3 py-2 text-[10px] text-gray-500 border-b border-gray-100">
            <span className="font-medium text-gray-600">Freelance monthly</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-yellow-100 border border-yellow-200" /> to pay</span>
            <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-sm bg-orange-100 border border-orange-200" /> pending (unsure)</span>
          </div>
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr style={{ backgroundColor: "#e8a838" }}>
                <th className="px-3 py-2 text-left text-white font-medium w-44 border-r border-orange-400/30" />
                {MONTHS.map((month, monthIndex) => (
                  <th key={month} className="px-2 py-2 text-center text-white font-medium border-r border-orange-400/30" style={{ minWidth: 72 }}>
                    <div>{month} {year}</div>
                    <div className="text-[10px] font-normal opacity-80">{isForecast(monthIndex) ? "Forecast" : "Actuals"}</div>
                  </th>
                ))}
                <th className="px-2 py-2 text-center text-white font-medium border-r border-orange-400/30" style={{ minWidth: 80 }}>
                  <div>Year to Date</div>
                  <div className="text-[10px] font-normal opacity-80">Actuals</div>
                </th>
                <th className="px-2 py-2 text-center text-white font-medium" style={{ minWidth: 80 }}>
                  <div>Projected</div>
                  <div className="text-[10px] font-normal opacity-80">Full Year</div>
                </th>
              </tr>
            </thead>
            <tbody>
              <SectionHeader label="Income" />
              <Row label="Invoicing" values={computed.income} isForecast={isForecast} proj={projTotal(computed.income)} ytd={ytd(computed.income)} indent onCellClick={onCellClick} />
              <Row label="Grants" values={computed.grants} isForecast={isForecast} proj={projTotal(computed.grants)} ytd={ytd(computed.grants)} indent onCellClick={onCellClick} />
              <TotalRow label="Total Income" values={computed.totalIncome} isForecast={isForecast} proj={projTotal(computed.totalIncome)} ytd={ytd(computed.totalIncome)} />

              <Spacer />

              <SectionHeader label="Expenses" />
              <Row label="Employee Costs" values={computed.employeeCosts} isForecast={isForecast} proj={projTotal(computed.employeeCosts)} ytd={ytd(computed.employeeCosts)} indent onCellClick={onCellClick} />
              <Row label="Freelance Monthly Costs" values={computed.freelanceMonthlyCosts} statuses={computed.freelanceMonthlyStatus} isForecast={isForecast} proj={projTotal(computed.freelanceMonthlyCosts)} ytd={ytd(computed.freelanceMonthlyCosts)} indent onCellClick={onCellClick} />
              <Row label="Freelance Daily Costs" values={computed.freelanceCosts} isForecast={isForecast} proj={projTotal(computed.freelanceCosts)} ytd={ytd(computed.freelanceCosts)} indent onCellClick={onCellClick} />
              <Row label="Other Costs" values={computed.fixedCosts} isForecast={isForecast} proj={projTotal(computed.fixedCosts)} ytd={ytd(computed.fixedCosts)} indent onCellClick={onCellClick} />
              <Row label="Business Introducer Costs" values={computed.businessIntroducer} isForecast={isForecast} proj={projTotal(computed.businessIntroducer)} ytd={ytd(computed.businessIntroducer)} indent onCellClick={onCellClick} />
              <TotalRow label="Total Expense" values={computed.totalExpense} isForecast={isForecast} proj={projTotal(computed.totalExpense)} ytd={ytd(computed.totalExpense)} />

              <Spacer />

              <HighlightRow label="Operating Profit" values={computed.operatingProfit} isForecast={isForecast} proj={projTotal(computed.operatingProfit)} ytd={ytd(computed.operatingProfit)} />
              <Row label="Loan" values={computed.loan} isForecast={isForecast} proj={projTotal(computed.loan)} ytd={ytd(computed.loan)} indent onCellClick={onCellClick} />
              <Row label="Taxes" values={computed.taxes} isForecast={isForecast} proj={projTotal(computed.taxes)} ytd={ytd(computed.taxes)} indent onCellClick={onCellClick} />
              <HighlightRow label="Net Profit" values={computed.netProfit} isForecast={isForecast} proj={projTotal(computed.netProfit)} ytd={ytd(computed.netProfit)} border />
            </tbody>
          </table>
        </div>
      )}

      <PnlDetailModal
        isOpen={modal.open}
        onClose={() => setModal({ open: false, rowLabel: null, monthIndex: null })}
        rowLabel={modal.rowLabel}
        monthIndex={modal.monthIndex}
        year={year}
        rawData={rawData}
      />
    </div>
  );
}

function SectionHeader({ label }) {
  return (
    <tr>
      <td className="px-3 pt-3 pb-1 font-semibold text-gray-800 text-sm" colSpan={15}>{label}</td>
    </tr>
  );
}

function Row({ label, values, isForecast, ytd, proj, indent, onCellClick, statuses }) {
  return (
    <tr className="hover:bg-gray-50/50">
      <td className={`px-3 py-1 text-gray-600 ${indent ? "pl-6" : ""}`}>{label}</td>
      {values.map((amount, monthIndex) => (
        <td
          key={monthIndex}
          onClick={() => onCellClick?.(label, monthIndex)}
          className={`px-2 py-1 text-right tabular-nums cursor-pointer hover:bg-blue-50 ${amount < 0 ? "text-red-600" : isForecast(monthIndex) ? "text-gray-400" : "text-gray-700"} ${isForecast(monthIndex) ? "italic" : ""} ${STATUS_TONE[statuses?.[monthIndex]] || ""}`}>
          {fmt(amount)}
        </td>
      ))}
      <td className={`px-2 py-1 text-right tabular-nums border-l border-gray-200 ${ytd < 0 ? "text-red-600" : "text-gray-500"}`}>{fmt(ytd)}</td>
      <td className={`px-2 py-1 text-right tabular-nums ${proj < 0 ? "text-red-600" : "text-gray-500"}`}>{fmt(proj)}</td>
    </tr>
  );
}

function TotalRow({ label, values, isForecast, ytd, proj }) {
  return (
    <tr className="border-t border-gray-300">
      <td className="px-3 py-1.5 font-bold text-gray-900">{label}</td>
      {values.map((amount, monthIndex) => (
        <td key={monthIndex} className={`px-2 py-1.5 text-right tabular-nums font-bold ${amount < 0 ? "text-red-600" : isForecast(monthIndex) ? "text-gray-400" : "text-gray-900"} ${isForecast(monthIndex) ? "italic" : ""}`}>{fmt(amount)}</td>
      ))}
      <td className={`px-2 py-1.5 text-right tabular-nums font-bold border-l border-gray-200 ${ytd < 0 ? "text-red-600" : "text-gray-700"}`}>{fmt(ytd)}</td>
      <td className={`px-2 py-1.5 text-right tabular-nums font-bold ${proj < 0 ? "text-red-600" : "text-gray-700"}`}>{fmt(proj)}</td>
    </tr>
  );
}

function HighlightRow({ label, values, isForecast, ytd, proj, border }) {
  return (
    <tr className={`bg-orange-50 ${border ? "border-t-2 border-orange-300" : "border-t border-orange-200"}`} style={{ borderTopColor: "#e8a838" }}>
      <td className="px-3 py-2.5 font-bold text-sm text-gray-900">{label}</td>
      {values.map((amount, monthIndex) => (
        <td key={monthIndex} className={`px-2 py-2.5 text-right tabular-nums font-bold text-sm ${amount < 0 ? "text-red-600" : "text-green-600"} ${isForecast(monthIndex) ? "italic opacity-70" : ""}`}>
          {fmt(amount)}
        </td>
      ))}
      <td className={`px-2 py-2.5 text-right tabular-nums font-bold text-sm border-l border-gray-200 ${ytd < 0 ? "text-red-600" : "text-green-600"}`}>{fmt(ytd)}</td>
      <td className={`px-2 py-2.5 text-right tabular-nums font-bold text-sm ${proj < 0 ? "text-red-600" : "text-green-600"}`}>{fmt(proj)}</td>
    </tr>
  );
}

function Spacer() {
  return <tr className="h-2" />;
}
